import { Theme } from '@mui/material';
import { createStyles, withStyles, WithStyles } from "@mui/styles";
import { galleryStyles, ImageListStyleClasses } from 'assets/jss/common-styles';
import Gallery from 'components/Gallery';
import { ReactLightBoxGallery } from 'components/LightBox/react-lightbox-gallery';
import MuiClickable from 'components/MuiExtras/MuiClickable';
import MediaUtils from 'lib/MediaUtils';
import { ContentMetadataGetDto, FileThumbnaildGetDto } from 'models/ApiModels';
import { AttachType } from 'models/AppModels';
import * as React from 'react';
import RenderFile from 'views/Publication/Components/RenderFile';
import RenderMetadata from 'views/Publication/Components/RenderMetadata';
import RenderVideo, { RenderVideoEventsProps } from 'views/Publication/Components/RenderVideo';

/////////////////////////////////////////////////////////////////////////////////////////////////////////////

const styles = (theme: Theme) => createStyles({
    container: {
    },

    galleryContainer: {
        backgroundColor: theme.palette.grey[200],
        padding: "8px 4px"
    },

    ...ImageListStyleClasses(theme)
});

/////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IProps {
    fileOrLink?: FileThumbnaildGetDto[] | ContentMetadataGetDto;
    autoplay?: boolean;
    autoCompleteCourse?: boolean;
    onCustomAction?: (action: any) => Promise<boolean>;
}

type Props = IProps & RenderVideoEventsProps & WithStyles<typeof styles>;


interface IState {
    lighBoxOpen: boolean;
}
/////////////////////////////////////////////////////////////////////////////////////////////////////////////

class AttachMetadataBox extends React.PureComponent<Props, IState> {

    constructor(props: Props) {
        super(props);
        this.state = { lighBoxOpen: false };
    }

    private renderSingleImage(img: FileThumbnaildGetDto) {
        const attach = img.resizedImage ?? img.mainFile;
        const maxW = 640, maxH = 360;
        let w = maxW, h = maxH;
        const url = attach.url;

        if (attach.metadata && attach.metadata.width && attach.metadata.height) {

            if (attach.metadata.width > attach.metadata.height) {
                w = attach.metadata.width > maxW ? maxW : attach.metadata.width;
                h = Math.round(attach.metadata.height * (w / attach.metadata.width));
            }
            else {
                h = attach.metadata.height > maxH ? maxH : attach.metadata.height;
                w = Math.round(attach.metadata.width * (h / attach.metadata.height));
            }
        }

        const outerStyles = { display: "flex", justifyContent: "center" };
        const innerStyles = { height: h, width: w };

        return (
            <div style={outerStyles}>
                <div style={innerStyles}>
                    <MuiClickable
                        pointer="zoomIn"
                        onClick={() => this.setState({ lighBoxOpen: true })}
                    >
                        <img src={url} alt={attach?.metadata?.title ?? ""} className={this.props.classes.imageFit} />
                    </MuiClickable>

                    <ReactLightBoxGallery
                        index={0}
                        images={[img.mainFile]}
                        thumbnails={[img.thumbnail || img.mainFile]}
                        isOpen={this.state.lighBoxOpen}
                        onClose={() => this.setState({ lighBoxOpen: false })}
                    />
                </div>
            </div >
        );
    }

    private renderGallery(files: FileThumbnaildGetDto[]) {
        return (
            <div className={this.props.classes.galleryContainer}>
                <Gallery
                    images={
                        files.map(({ mainFile, thumbnail }, index) => ({
                            src: mainFile.url,
                            thumbnail: thumbnail && thumbnail.url
                                ? thumbnail.url
                                : mainFile && mainFile.url
                                    ? mainFile.url
                                    : null,
                            caption: "",
                            orientation: "",
                            useForDemo: index < 3,
                        }))
                    }
                    showThumbnails
                    backdropClosesModal
                    theme={galleryStyles}
                />
            </div>
        );
    }

    render() {
        if (this.props.fileOrLink != null) {

            const { classes, autoplay, onCustomAction, autoCompleteCourse, ...rest } = this.props;

            if (this.props.fileOrLink.hasOwnProperty("provider")) {
                const metadata = this.props.fileOrLink as ContentMetadataGetDto;
                return <div className={classes.container}><RenderMetadata metadata={metadata} onCustomAction={onCustomAction} autoCompleteCourse={autoCompleteCourse} {...rest} /></div>;
            }
            else {
                const attachs = (this.props.fileOrLink as FileThumbnaildGetDto[]);
                if (attachs.length > 0) {
                    const attachType = MediaUtils.getAttachTypeFromMimeType(attachs[0].mainFile.mimeType);

                    if (attachType == AttachType.Image) {
                        const hasEmbed = attachs.findIndex(p => p.mainFile.isEmbed === true) > -1;
                        const attachList = attachs.filter(p => !p.mainFile.isEmbed);

                        return (
                            <div className={classes.container}>
                                {attachList.length == 1 && !hasEmbed ? this.renderSingleImage(attachList[0]) : this.renderGallery(attachList)}
                            </div>
                        );
                    }

                    if (attachType == AttachType.Video) {
                        return <div className={classes.container}><RenderVideo file={attachs[0].mainFile} autoplay={autoplay || false} {...rest} /></div>;
                    }

                    if (attachType == AttachType.File) {
                        return <div className={classes.container}><RenderFile file={attachs[0].mainFile} onCustomAction={onCustomAction} autoCompleteCourse={autoCompleteCourse} enableDownload  /></div>;
                    }
                }
            }
        }

        return null;
    }
}

export default withStyles(styles)(AttachMetadataBox);